import { BadgeCheck, RefreshCw } from 'lucide-react';
import * as React from 'react';

import { cn, createTestIdHelper } from '../../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';

import {
  emrSyncIndicatorVariants,
  emrSyncMismatchOverlayVariants,
  emrSyncPillIconVariants,
  emrSyncPillMismatchOverlayVariants,
  emrSyncPillVariants,
} from './variants';

const testId = createTestIdHelper('emrSyncIndicator');

/**
 * Patient Sync Status as rendered. Only two states exist: a patient is either
 * linked to an EMR profile or awaiting that link. "Mismatch Detected" is NOT a
 * third state — it is an overlay on `synchronized`, because a patient with
 * unresolved field conflicts is still linked.
 */
export type EmrSyncIndicatorState = 'synchronized' | 'pending';

/**
 * How much room the indicator is given.
 *
 * `icon` is the dense default — a 32px square used in table cells and inline
 * beside a patient name, where the state is carried by colour + glyph.
 * `pill` names the state outright for roomy surfaces such as a form section
 * header. Same states and same click target either way; only the density differs.
 */
export type EmrSyncIndicatorVariant = 'icon' | 'pill';

export interface EmrSyncIndicatorProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  state: EmrSyncIndicatorState;
  /** Density. Defaults to `icon` so every existing dense consumer is unchanged. */
  variant?: EmrSyncIndicatorVariant;
  /**
   * Visible state label for the `pill` variant, e.g. "Synchronized". Required
   * there and ignored by `icon`, which has no room for text.
   */
  label?: string;
  /**
   * Renders the conflict marker on the badge. Only meaningful together with
   * `synchronized`: an unlinked patient has no EMR value to disagree with.
   */
  hasMismatch?: boolean;
  /**
   * Absolute datetime for the native tooltip. The badge carries no visible
   * timestamp, so hover is where the precise last-sync time lives. Ignored once
   * `tooltipTitle` is given — the richer tooltip then owns hover, and keeping
   * both would stack a native tooltip on top of it.
   */
  syncedAtTitle?: string;
  /**
   * Headline of the hover tooltip, e.g. "Pending Sync". The badge is a bare
   * glyph on most surfaces, so this is what tells a sighted user which state
   * they are looking at without having to decode colour + icon.
   */
  tooltipTitle?: string;
  /** Second tooltip line: what the state means, or when the patient last synced. */
  tooltipDescription?: string;
  /**
   * Accessible name. Required because the badge renders no visible text — the
   * state is conveyed by colour and icon, which a screen reader cannot see, and
   * colour alone must never be the only carrier of state.
   */
  ariaLabel: string;
  /** Accessible description of the conflict marker, announced when present. */
  mismatchLabel?: string;
  className?: string;
}

const STATE_ICONS: Record<EmrSyncIndicatorState, typeof BadgeCheck> = {
  synchronized: BadgeCheck,
  // A refresh glyph, not a clock: the pending state is not "wait for this to
  // happen on its own", it is "this patient still needs to be linked" — an
  // action the badge itself opens. A clock read as a passive timer.
  pending: RefreshCw,
};

/**
 * EMR Sync Indicator — the shared, presentation-only badge for a patient's sync
 * state, used anywhere patient identity is shown (onboarding side panel, new
 * implants list, patient header).
 *
 * It holds NO copy and NO data fetching: every label arrives as a prop so the
 * consuming app owns translation, and the state arrives resolved so this component
 * has one job — draw it. A consumer that has nothing to show renders nothing rather
 * than asking this component to decide.
 */
export const EmrSyncIndicator = React.forwardRef<
  HTMLButtonElement,
  EmrSyncIndicatorProps
>(function EmrSyncIndicator(
  {
    state,
    variant = 'icon',
    label,
    hasMismatch = false,
    syncedAtTitle,
    tooltipTitle,
    tooltipDescription,
    ariaLabel,
    mismatchLabel,
    className,
    ...props
  },
  ref,
) {
  // Fall back rather than crash: this is an exported design-system primitive, so a
  // consumer (or a future state added to the DTO before this component catches up)
  // can hand it a value outside the union. Degrade to the same default the cva
  // variants use, so the badge still renders instead of throwing at `<Icon />`.
  const Icon = STATE_ICONS[state] ?? STATE_ICONS.synchronized;
  // Conflicts are only coherent on a linked patient; guard here so a consumer
  // cannot render a "conflict" marker on a badge that shows no EMR value.
  const showMismatch = hasMismatch && state === 'synchronized';

  /**
   * Wraps whichever density was rendered in the hover tooltip, so both variants
   * describe themselves the same way instead of each inventing its own hover.
   * Without `tooltipTitle` the trigger is returned untouched and the native
   * `title` stays the only hover, keeping existing consumers unchanged.
   */
  const withTooltip = (trigger: React.ReactElement) => {
    if (!tooltipTitle) {
      return trigger;
    }

    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          {/* Capped so a two-line description wraps into a compact block instead
              of stretching the bubble across the viewport — the badge sits in
              dense rows and page headers where a full-width tooltip covers the
              content it is describing. */}
          <TooltipContent
            side="top"
            align="center"
            className="max-w-xs"
            data-testid={testId('tooltip')}
          >
            <p className="text-sm font-semibold">{tooltipTitle}</p>
            {tooltipDescription && (
              <p className="text-sm text-muted-foreground">
                {tooltipDescription}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (variant === 'pill') {
    return withTooltip(
      <button
        ref={ref}
        type="button"
        aria-label={
          showMismatch && mismatchLabel
            ? `${ariaLabel} — ${mismatchLabel}`
            : ariaLabel
        }
        title={tooltipTitle ? undefined : syncedAtTitle}
        className={cn(emrSyncPillVariants({ state }), className)}
        data-testid={testId('trigger')}
        {...props}
      >
        {/* aria-hidden: the accessible name already carries the state, so reading
            the label again would announce it twice. */}
        <span aria-hidden>{label}</span>
        {/* The SAME state glyph the dense variant draws, so the two densities read
            as one badge, with the conflict marker on its corner exactly as there. */}
        <span className={emrSyncPillIconVariants()}>
          <Icon className="size-4" strokeWidth={2.5} aria-hidden />
          {showMismatch && (
            <span
              className={emrSyncPillMismatchOverlayVariants()}
              data-testid={testId('mismatch')}
              aria-hidden
            />
          )}
        </span>
      </button>,
    );
  }

  return (
    <span
      className={cn('inline-flex items-center', className)}
      data-testid={testId('root')}
    >
      {/* ONE interactive element for the whole indicator (FR-NI-204). */}
      {withTooltip(
        <button
          ref={ref}
          type="button"
          aria-label={
            showMismatch && mismatchLabel
              ? `${ariaLabel} — ${mismatchLabel}`
              : ariaLabel
          }
          title={tooltipTitle ? undefined : syncedAtTitle}
          className={emrSyncIndicatorVariants({ state })}
          data-testid={testId('trigger')}
          {...props}
        >
          <Icon className="size-3.5" strokeWidth={2.5} aria-hidden />
          {showMismatch && (
            <span
              className={emrSyncMismatchOverlayVariants()}
              data-testid={testId('mismatch')}
              aria-hidden
            />
          )}
        </button>,
      )}
    </span>
  );
});
